import {
  Syne_600SemiBold,
  Syne_700Bold,
  Syne_800ExtraBold,
} from '@expo-google-fonts/syne';
import {
  Outfit_400Regular,
  Outfit_500Medium,
  Outfit_600SemiBold,
  Outfit_700Bold,
} from '@expo-google-fonts/outfit';
import { useAppearance, type ResolvedAppearanceMode } from '@/lib/design/appearance';

export const APP_FONTS = {
  Syne_600SemiBold,
  Syne_700Bold,
  Syne_800ExtraBold,
  Outfit_400Regular,
  Outfit_500Medium,
  Outfit_600SemiBold,
  Outfit_700Bold,
};

const DAY_COLORS = {
  canvas: '#fff8e7',
  canvasAlt: '#fff1cf',
  canvasInset: '#f7e7bc',
  surface: '#fffaf0',
  surfaceAlt: '#fdf0ca',
  surfaceRaised: '#fff2d9',
  surfaceOverlay: 'rgba(255, 248, 229, 0.96)',
  surfaceDock: '#fff5df',
  surfaceAccent: '#ffe5a8',
  border: '#ead7a2',
  borderDock: '#dfca8a',
  borderStrong: '#d7bd72',
  text: '#2f2413',
  textMuted: '#5e4b23',
  textSoft: '#7a663a',
  textDockMuted: '#705d33',
  textInverse: '#2f2413',
  accent: '#d99212',
  accentStrong: '#efb229',
  accentPressed: '#bf7e08',
  accentSoft: 'rgba(239, 178, 41, 0.14)',
  accentSolidMuted: '#f4ddb0',
  accentEdge: 'rgba(239, 178, 41, 0.32)',
  mapOverlay: 'rgba(255, 246, 221, 0.84)',
  success: '#4d9f61',
  cool: '#4b93d9',
  sceneGradientStart: '#fff9e8',
  sceneGradientMid: '#fff3cf',
  sceneGradientEnd: '#f7e2a8',
  sceneOverlay: 'rgba(255, 250, 237, 0.52)',
  sceneTopGlow: 'rgba(255, 225, 138, 0.28)',
  ambientWarm: 'rgba(255, 193, 74, 0.24)',
  ambientCool: 'rgba(121, 182, 255, 0.16)',
  ambientOverlay: 'rgba(255, 248, 231, 0.28)',
} as const;

const NIGHT_COLORS = {
  canvas: '#080808',
  canvasAlt: '#0d0d0d',
  canvasInset: '#161410',
  surface: '#111008',
  surfaceAlt: '#17130d',
  surfaceRaised: '#1a1710',
  surfaceOverlay: 'rgba(26, 23, 16, 0.94)',
  surfaceDock: '#080808',
  surfaceAccent: '#24190c',
  border: '#1e1c14',
  borderDock: '#1e1c14',
  borderStrong: '#3a3120',
  text: '#f0ece4',
  textMuted: '#b8ae9f',
  textSoft: '#d2c7b5',
  textDockMuted: '#b8ae9f',
  textInverse: '#0d0d0d',
  accent: '#ef4e2e',
  accentStrong: '#ef4e2e',
  accentPressed: '#ff6f49',
  accentSoft: 'rgba(239, 78, 46, 0.14)',
  accentSolidMuted: '#1a1308',
  accentEdge: 'rgba(239, 78, 46, 0.48)',
  mapOverlay: 'rgba(8, 8, 8, 0.84)',
  success: '#9ACD68',
  cool: '#70C6FF',
  sceneGradientStart: '#000000',
  sceneGradientMid: '#000000',
  sceneGradientEnd: '#000000',
  sceneOverlay: 'rgba(0, 0, 0, 0.96)',
  sceneTopGlow: 'rgba(0, 0, 0, 0)',
  ambientWarm: 'rgba(0, 0, 0, 0)',
  ambientCool: 'rgba(0, 0, 0, 0)',
  ambientOverlay: 'rgba(0, 0, 0, 0)',
} as const;

const DAY_SHADOWS = {
  card: {
    shadowColor: '#a97816',
    shadowOpacity: 0.12,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  overlay: {
    shadowColor: '#8f6d19',
    shadowOpacity: 0.16,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 10 },
    elevation: 10,
  },
  dock: {
    shadowColor: '#9a7220',
    shadowOpacity: 0.16,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
} as const;

const NIGHT_SHADOWS = {
  card: {
    shadowColor: '#000000',
    shadowOpacity: 0.5,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 10 },
    elevation: 12,
  },
  overlay: {
    shadowColor: '#000000',
    shadowOpacity: 0.38,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 14 },
    elevation: 14,
  },
  dock: {
    shadowColor: '#000000',
    shadowOpacity: 0.28,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 10 },
    elevation: 10,
  },
} as const;

const STATIC_TOKENS = {
  spacing: {
    screen: 16,
    section: 28,
    stack: 18,
    compact: 10,
  },
  radii: {
    screen: 32,
    card: 26,
    cardCompact: 20,
    button: 18,
    pill: 999,
  },
  typeScale: {
    hero: 40,
    title: 26,
    section: 20,
    body: 15,
    bodySmall: 13,
    label: 10,
    eyebrow: 10,
  },
  typography: {
    fonts: {
      displaySemiBold: 'Syne_700Bold',
      displayBold: 'Syne_800ExtraBold',
      displayExtraBold: 'Syne_800ExtraBold',
      body: 'Outfit_400Regular',
      bodyMedium: 'Outfit_500Medium',
      bodySemiBold: 'Outfit_600SemiBold',
      bodyBold: 'Outfit_700Bold',
    },
  },
} as const;

export const THEME_COLORS = {
  day: DAY_COLORS,
  night: NIGHT_COLORS,
} as const;

export const THEME_SHADOWS = {
  day: DAY_SHADOWS,
  night: NIGHT_SHADOWS,
} as const;

function getCurrentMode(): ResolvedAppearanceMode {
  return useAppearance.getState().resolvedMode;
}

function createDynamicSection<T extends Record<string, any>>(
  getter: (mode: ResolvedAppearanceMode) => T
): T {
  const sample = getter('night');
  const dynamicSection = {} as T;

  for (const key of Object.keys(sample) as (keyof T)[]) {
    Object.defineProperty(dynamicSection, key, {
      enumerable: true,
      configurable: false,
      get() {
        return getter(getCurrentMode())[key];
      },
    });
  }

  return dynamicSection;
}

export function getDesignTokens(mode: ResolvedAppearanceMode = getCurrentMode()) {
  return {
    colors: THEME_COLORS[mode],
    shadows: THEME_SHADOWS[mode],
    spacing: STATIC_TOKENS.spacing,
    radii: STATIC_TOKENS.radii,
    typeScale: STATIC_TOKENS.typeScale,
    typography: STATIC_TOKENS.typography,
  };
}

export const DESIGN_TOKENS = {
  colors: createDynamicSection((mode) => THEME_COLORS[mode]),
  shadows: createDynamicSection((mode) => THEME_SHADOWS[mode]),
  spacing: STATIC_TOKENS.spacing,
  radii: STATIC_TOKENS.radii,
  typeScale: STATIC_TOKENS.typeScale,
  typography: STATIC_TOKENS.typography,
} as const;

export function resolveWeightFont(className?: string) {
  if (!className) {
    return DESIGN_TOKENS.typography.fonts.body;
  }

  if (className.includes('font-extrabold') || className.includes('font-bold')) {
    return DESIGN_TOKENS.typography.fonts.bodyBold;
  }

  if (className.includes('font-semibold')) {
    return DESIGN_TOKENS.typography.fonts.bodySemiBold;
  }

  if (className.includes('font-medium')) {
    return DESIGN_TOKENS.typography.fonts.bodyMedium;
  }

  return DESIGN_TOKENS.typography.fonts.body;
}
